import React, {useEffect, useRef, useState} from 'react';
import useApi from "../../hooks/useApi";
import useDelete from "../../hooks/useDelete";
import {webInfo} from "../../utils/webInfo";
import {Paginator} from "primereact/paginator";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Card} from "primereact/card";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Toast} from "primereact/toast";
import callToast, {catchError} from "../../utils/helper";
import EmptyData from "../../components/EmptyData";
import TableSkeleton from "../../Loader/Loader";
import ErrorPresentor from "../../ErrorPresentor/ErrorPresentor";

const CvBank = () => {
    const {get,loading, error} = useApi<any>();
    const {remove, loading: deleteLoading} = useDelete();
    const toast = useRef<Toast | null>(null);
    const [total, setTotal] = useState<any>(0);
    const [page, setPage] = useState<any>(1);
    const [perPage] = useState(10);
    const [cvData, setCvData] = useState<any>([]);
    const [expandedRows, setExpandedRows] = useState<any>(null);

    const [selectedCv, setSelectedCv] = useState<any>(null);
    const [visibleConfirmation, setVisibleConfirmation] = useState(false);
    const [refresh, setRefresh] = useState(false);

    const url = `${webInfo.baseUrl}/api/admin/cv-bank?page=${page}&per_page=${perPage}`;

    const onPageChange = (event: any) => {
        setPage(event.page + 1);
    };

    useEffect(() => {
        const getCvBank = async () => {
            try {
                const res: any = await get(url);

                const responseData = res?.data ?? res;
                const cvList = responseData?.data ?? [];

                setCvData(cvList);
                setTotal(responseData?.total ?? cvList.length);
            } catch (er) {
                console.error("Failed to fetch CV bank data", er);
            }
        };

        getCvBank();

        // eslint-disable-next-line
    }, [page, perPage, refresh]);

    const openDeleteDialog = (cv: any) => {
        setSelectedCv(cv);
        setVisibleConfirmation(true);
    };

    const closeDeleteDialog = () => {
        setSelectedCv(null);
        setVisibleConfirmation(false);
    };

    const deleteCv = async () => {
        if (!selectedCv) return;

        const deleteUrl = `${webInfo.baseUrl}/api/admin/cv-bank/${selectedCv.id}`;

        try {
            const res: any = await remove(deleteUrl);

            if (res?.success === false) {
                callToast(toast, false, res?.error?.message ?? "Failed to delete CV");
                return;
            }

            callToast(toast, true, "CV deleted successfully");
            closeDeleteDialog();
            setRefresh(!refresh);
        } catch (e) {
            catchError(e);
            callToast(toast, false, "Failed to delete CV");
        }
    };

    const rowExpansionTemplate = (data: any) => {
        return (
            <div className="p-3 grid">
                <div className="col-12 md:col-6">
                    <h6>Education</h6>

                    <div className="grid">
                        <div className="col-4">Institute</div>
                        <div className="col-8">: {data?.last_educational_institution || "-"}</div>
                    </div>

                    <div className="grid">
                        <div className="col-4">Degree</div>
                        <div className="col-8">: {data?.last_educational_degree || "-"}</div>
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <h6>Contact</h6>

                    <div className="grid">
                        <div className="col-4">Phone</div>
                        <div className="col-8">: {data?.mobile_no || data?.phone || "-"}</div>
                    </div>

                    <div className="grid">
                        <div className="col-4">Email</div>
                        <div className="col-8">: {data?.email || "-"}</div>
                    </div>
                </div>

                <div className="col-12 md:col-6 mt-3">
                    <h6>Experience</h6>

                    <div className="grid">
                        <div className="col-4">Current Company</div>
                        <div className="col-8">: {data?.last_present_company || "-"}</div>
                    </div>

                    <div className="grid">
                        <div className="col-4">Position</div>
                        <div className="col-8">: {data?.position || "-"}</div>
                    </div>
                </div>

                <div className="col-12 md:col-6 mt-3">
                    <h6>Location</h6>

                    <div className="grid">
                        <div className="col-4">District</div>
                        <div className="col-8">: {data?.district || "-"}</div>
                    </div>
                </div>
            </div>
        );
    };

    const cvTemplate = (data: any) => {
        if (!data?.resume) {
            return "-";
        }

        const resumeUrl = data.resume.startsWith("http")
            ? data.resume
            : `${webInfo.baseUrl}/${data.resume}`;

        return (
            <a
                href={resumeUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
            >
                <Button type="button" size="small">
                    <i className="pi pi-download mr-2"></i>
                    Download
                </Button>
            </a>
        );
    };

    const actionTemplate = (rowData: any) => (
        <div className="flex gap-2">
            <Button
                icon="pi pi-trash"
                title="Delete"
                className="p-button-sm p-button-danger"
                loading={deleteLoading && selectedCv?.id === rowData.id}
                onClick={() => openDeleteDialog(rowData)}
            />
        </div>
    );

    const deleteFooterContent = (
        <div className="flex justify-content-end align-items-center">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={closeDeleteDialog}
            />

            <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                className="ml-2"
                loading={deleteLoading}
                onClick={deleteCv}
            />
        </div>
    );

    const dataTable = <div>
        {
            (cvData?.length>0 && !loading) ? <div>
                    <DataTable
                        value={cvData}
                        rows={perPage}
                        stripedRows
                        emptyMessage="No CV found"
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={rowExpansionTemplate}
                        dataKey="id"
                        tableStyle={{minWidth: '60rem'}}
                    >
                        <Column expander={true} style={{width: '5rem'}}/>
                        <Column field="name" header="Name"/>
                        <Column field="position" header="Position"/>
                        <Column field="year_of_experience" header="Experience(Yr)"/>
                        <Column field="expected_salary" header="Expected Salary"/>
                        <Column field="division" header="Division"/>
                        <Column field="department" header="Department"/>
                        <Column body={cvTemplate} header="Attachment"/>
                        <Column body={actionTemplate} header="Action"/>
                    </DataTable>

                    <Paginator
                        first={(page - 1) * perPage}
                        rows={perPage}
                        totalRecords={total}
                        rowsPerPageOptions={[10, 20, 30]}
                        onPageChange={onPageChange}
                    />
                </div> :
                <EmptyData/>
        }
    </div>

    const header = (
        <div className='flex align-items-center justify-content-between p-4 pb-0'>
            <h4 className="m-0">CV Bank</h4>
        </div>
    )

    return (
        <Card header={header}>
            <Toast ref={toast}/>
            <Dialog
                visible={visibleConfirmation}
                modal
                header="Delete CV Confirmation"
                footer={deleteFooterContent}
                style={{width: '30rem'}}
                onHide={closeDeleteDialog}
            >
                <p className="m-0">
                    Are you sure you want to delete this CV?
                    Once deleted, it cannot be recovered.
                </p>

                {selectedCv && (
                    <div className="mt-3">
                        <strong>Applicant:</strong> {selectedCv?.name || "-"}
                    </div>
                )}
            </Dialog>

            {loading ? (<TableSkeleton/>) : (<div>{error ? <ErrorPresentor error={error}/> : dataTable}</div>)}
        </Card>
    );
};

export default CvBank;